import React, { useCallback, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import { Box, Grid, IconButton, Typography } from "@mui/material";
import { Shield, KeyRound } from "lucide-react";
import { Cancel } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import { useForm } from "react-hook-form";

import Button from "./Button";
import CustomPasswordTextField from "./CustomPasswordTextField";
import CustomTextField from "./CustomTextField";
import { useModalStyles } from "./useModalStyles";

import {
  SingleAlertInfo,
  setSingleAlertObj,
} from "../../redux/slice/commonSlice";

import { useAppDispatch } from "../../redux/slice/redux-hooks";

import {
  ResetPassword,
  clearResetPasswordObject,
  resetPassword,
  useUserReducer,
} from "../../redux/slice/userSlice";

interface PasswordResetProps {
  open: boolean;
  onClose: () => void;
  email: string;
  userId: number;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const PasswordResetPopup: React.FC<PasswordResetProps> = ({
  open,
  onClose,
  email,
  userId,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({});

  const dispatch = useAppDispatch();
  const { resetPasswordObject } = useUserReducer();

  const {
    paperSx,
    headerSx,
    bodySx,
    footerSx,
    tokens,
    cancelBtnSx,
    primaryBtnSx,
    inputFieldSx,
    iconBoxSx,
    closeIconSx,
  } = useModalStyles();

  const FieldLabel = ({
    label,
    required = false,
  }: {
    label: string;
    required?: boolean;
  }) => (
    <Typography
      sx={{
        fontSize: "0.8rem",
        fontWeight: 600,
        color: tokens.textSecondary,
        mb: 0.7,
        display: "flex",
        alignItems: "center",
        gap: 0.3,
      }}
    >
      {label}
      {required && (
        <Box component="span" sx={{ color: tokens.accent }}>
          *
        </Box>
      )}
    </Typography>
  );

  const defaultValues = useCallback(() => {
    if (resetPasswordObject) {
      reset(resetPasswordObject);
    } else {
      reset({
        userId,
        email,
        password: "",
        confirmPassword: "",
      });
    }
  }, [resetPasswordObject, reset, email, userId]);

  const handleChangePassword = async (
    data: ResetPassword,
    successMessage: string,
    errorMessage: string
  ) => {
    const resetPWData = {
      userId,
      password: data.password,
      email: "",
      confirmPassword: "",
      oldPassword: "",
      passwordResetRequired: true,
    };

    try {
      const response = await dispatch(resetPassword(resetPWData));

      const alertObj: SingleAlertInfo = {
        message:
          response.meta.requestStatus === "fulfilled"
            ? successMessage
            : errorMessage,
        alertType:
          response.meta.requestStatus === "fulfilled"
            ? "success"
            : "error",
      };

      dispatch(setSingleAlertObj(alertObj));
      return response;
    } catch (error) {
      console.error("Error resetting password:", error);
      throw error;
    }
  };

  const onSubmit = async (data: any) => {
    try {
      await handleChangePassword(
        data,
        "Password Reset Successful.",
        "Password Reset Unsuccessful."
      );
    } catch (error) {}

    defaultValues();
    onClose();
  };

  const handleClose = () => {
    onClose();
    defaultValues();
  };

  useEffect(() => {
    defaultValues();
  }, [defaultValues]);

  useEffect(() => {
    return () => {
      dispatch(clearResetPasswordObject());
    };
  }, [dispatch]);

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: paperSx(false) }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={headerSx}>
          <Box sx={iconBoxSx(tokens.accent)}>
            <Shield size={20} color={tokens.accent} strokeWidth={1.8} />
          </Box>

          <Box sx={{ flex: 1, pr: 4 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.1rem",
                color: tokens.textPrimary,
                lineHeight: 1.3,
              }}
            >
              Reset Password
            </Typography>

            <Typography
              sx={{
                fontSize: "0.9rem",
                color: tokens.textSecondary,
                mt: 0.15,
              }}
            >
              Generate a secure temporary password
            </Typography>
          </Box>

          <IconButton onClick={handleClose} size="small" sx={closeIconSx} aria-label="Close">
            <CloseIcon sx={{ fontSize: "1rem" }} />
          </IconButton>
        </Box>

        <Box sx={bodySx}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FieldLabel label="Email" />
              <Box sx={inputFieldSx(!!errors.email)}>
                <CustomTextField
                  control={control}
                  controllerName="email"
                  label=""
                  size="small"
                  isDisabled={true}
                  value={email}
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <FieldLabel label="Temporary Password" required />
              <Box sx={inputFieldSx(!!errors.password)}>
                <CustomPasswordTextField
                  control={control}
                  controllerName="password"
                  label=""
                  size="small"
                  autoComplete="new-password"
                  rules={{
                    required: "Password is required.",
                    minLength: {
                      value: 12,
                      message:
                        "Password should be at least 12 characters.",
                    },
                    pattern: {
                      value:
                        /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])/,
                      message:
                        "Password must contain uppercase, number, and special character",
                    },
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <FieldLabel
                label="Confirm Temporary Password"
                required
              />
              <Box sx={inputFieldSx(!!errors.confirmPassword)}>
                <CustomPasswordTextField
                  control={control}
                  controllerName="confirmPassword"
                  label=""
                  size="small"
                  autoComplete="new-password"
                  rules={{
                    required: "Confirm Password is required.",
                    validate: (value: string) =>
                      value === watch("password") ||
                      "Passwords do not match",
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box sx={footerSx}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
              width: "100%",
              flexDirection: { xs: "column-reverse", sm: "row" },
            }}
          >
            <Button
              variant="outlined"
              removeUpperCase
              startIcon={<Cancel />}
              onClick={handleClose}
              fullWidth
              sx={{
                ...cancelBtnSx,
                minWidth: { sm: 120 },
                width: { xs: "100%", sm: "auto" },
                px: 2.5,
                "& .MuiTypography-root": { color: "inherit", fontWeight: 700 },
                "& .MuiButton-startIcon, & .MuiSvgIcon-root": { color: "inherit" },
              }}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              removeUpperCase
              startIcon={<KeyRound size={18} />}
              fullWidth
              sx={{
                ...primaryBtnSx,
                color: "#fff",
                minWidth: { sm: 160 },
                width: { xs: "100%", sm: "auto" },
                px: 2.5,
                "& .MuiTypography-root": {
                  color: "#fff",
                  fontWeight: 700,
                },
                "& .MuiButton-startIcon, & .MuiSvgIcon-root": { color: "#fff" },
              }}
            >
              Reset Password
            </Button>
          </Box>
        </Box>
      </form>
    </Dialog>
  );
};

export default PasswordResetPopup;
